import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { Seo } from "@/components/site/Seo";
import { SiteLayout } from "@/components/site/SiteLayout";
import { OrderForm } from "@/components/site/OrderForm";
import { SocialMarquee } from "@/components/site/SocialMarquee";
import { InstagramCard } from "@/components/site/InstagramCard";
import { Reviews } from "@/components/site/Reviews";
import { ScrollReveal } from "@/components/site/ScrollReveal";
import { Logo } from "@/components/site/Logo";
import { api } from "@/lib/api";
import { Zap, ShieldCheck, Headphones, TrendingUp, ArrowRight, Clock } from "lucide-react";

const FEATURES = [
  { Icon: Zap, title: "Anında İşlem", desc: "Siparişiniz ödeme sonrası otomatik işleme alınır." },
  { Icon: ShieldCheck, title: "Güvenli Ödeme", desc: "Shopier güvenli ödeme altyapısı ile korunursunuz." },
  { Icon: Headphones, title: "7/24 Destek", desc: "Uzman destek ekibimiz her zaman yanınızda." },
  { Icon: TrendingUp, title: "Gerçek Büyüme", desc: "Gerçek ve kaliteli sosyal medya hizmetleri." },
];

export default function Home() {
  const { settings } = useSettings();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const n = settings?.homepage_blog_count || 3;
    api.get(`/blog?limit=${n}`).then((r) => setPosts(r.data.items)).catch(() => {});
  }, [settings]);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Organization", name: "TakipciSatis.com",
    url: "https://takipcisatis.com", logo: "https://takipcisatis.com/takipcisatis-logo.png",
  };

  return (
    <SiteLayout>
      <Seo canonical="https://takipcisatis.com/" jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-10 pb-8 sm:pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <ScrollReveal>
              <div className="flex justify-center animate-float">
                <Logo className="h-24 sm:h-40 lg:h-52" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold mt-8 leading-tight text-white whitespace-pre-line">
                {settings?.hero_title || "Sosyal Medyada Hızlı ve Güvenli Büyü"}
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={220}>
              <p className="text-base sm:text-lg text-gray-400 mt-5 max-w-2xl mx-auto leading-relaxed">
                {settings?.hero_subtitle}
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ORDER FORM */}
      <section id="siparis" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 scroll-mt-20">
        <OrderForm />
      </section>

      {/* SOCIAL MARQUEE */}
      <section className="py-6">
        <SocialMarquee />
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map(({ Icon, title, desc }, i) => (
            <ScrollReveal key={i} delay={i * 80}>
              <div className="card-surface card-hover p-6 h-full" data-testid={`feature-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-4">
                  <Icon size={22} className="text-[#1f222d]" />
                </div>
                <h3 className="font-bold text-white text-lg">{title}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* INSTAGRAM CARD */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ScrollReveal><InstagramCard /></ScrollReveal>
      </section>

      {/* REVIEWS */}
      <Reviews />

      {/* BLOG */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-xs font-medium text-[#d8b4fe] uppercase tracking-[0.2em]">Blog</span>
              <h2 className="text-3xl font-extrabold text-white mt-2">Son Yazılar</h2>
            </div>
            <Link to="/blog" className="text-sm font-semibold text-gray-300 hover:text-white flex items-center gap-1" data-testid="home-blog-all">Tümü <ArrowRight size={16} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="card-surface card-hover overflow-hidden group" data-testid={`home-blog-${p.slug}`}>
                {p.cover_image && <div className="h-44 overflow-hidden"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" /></div>}
                <div className="p-5">
                  {p.category && <span className="text-xs text-[#d8b4fe] font-medium">{p.category}</span>}
                  <h3 className="font-bold text-white mt-1 line-clamp-2">{p.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 line-clamp-2">{p.excerpt}</p>
                  <span className="text-xs text-gray-500 mt-3 flex items-center gap-1"><Clock size={12} />{(p.published_at || "").slice(0, 10)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
